import {
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
  createTheme,
} from '@mui/material'

export const theme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    primary: {
      main: 'hsl(185, 100%, 16%)',
      light: 'hsl(187, 100%, 25%)',
      dark: 'hsl(187, 100%, 8%)',
      '50': 'hsl(187, 100%, 96%)',
      '100': 'hsl(187, 100%, 90%)',
      '200': 'hsl(187, 100%, 80%)',
      '300': 'hsl(187, 100%, 70%)',
      '400': 'hsl(187, 100%, 60%)',
      '500': 'hsl(187, 100%, 45%)',
      '600': 'hsl(187, 100%, 36%)',
      '700': 'hsl(187, 100%, 25%)',
      '800': 'hsl(187, 100%, 16%)',
      '900': 'hsl(187, 100%, 8%)',
    },
    secondary: {
      main: 'hsl(170, 69%, 55%)',
      light: 'hsl(170, 69%, 70%)',
      dark: 'hsl(170, 69%, 40%)',
    },
    text: {
      primary: 'hsl(187, 100%, 16%)',
      secondary: '#007681',
    },
    error: {
      main: '#EB6161',
    },
    warning: {
      main: '#FFC107',
    },
    info: {
      main: '#58C3FF',
    },
    success: {
      main: '#65C26F',
    },
    productSwitcher: {
      front: 'hsl(185, 100%, 16%)',
      back: 'hsl(170, 69%, 55%)',
      dashed: 'hsl(187, 16%, 72%)',
    },
    creditJourney: {
      switchTrack: '#f2f6f6',
      switchBorder: '#d3e0e2',
      switchOff: '#d3dcdd',
      switchOffText: '#3c5053',
      ribbon: '#d8f7f1',
      ribbonText: '#116b5c',
      benefitText: '#55696b',
      gain: '#2e8b3a',
      loss: '#c43c3c',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        paper: {
          padding: 0,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
      },
      styleOverrides: {
        containedSuccess: {
          color: '#fff',
        },
      },
    },
    MuiLoadingButton: {
      defaultProps: {
        variant: 'contained',
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: 16,
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#007681',
          cursor: 'pointer',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          padding: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          color: ownerState.color === 'default' ? 'GrayText' : '#fff',
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: 0,
        },
      },
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiDialogTitle: {
      defaultProps: {
        fontSize: '1.25rem',
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        placement: 'top',
        slotProps: {
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -14],
                },
              },
            ],
          },
        },
      },
    },
  },
  typography: {
    h1: {
      fontSize: '3rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  shadows: [
    'none',
    '0px 0px 1px 0px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 1px 1px -1px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 1px 3px -1px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 0px 4px 0px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 1px 5px 0px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 1px 5px 0px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 2px 5px -1px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 3px 5px -2px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 3px 6px -2px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 4px 6px -2px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 4px 7px -3px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 5px 8px -3px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 5px 8px -3px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 5px 9px -3px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 6px 9px -4px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 6px 10px -4px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 6px 11px -4px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 7px 11px -4px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 7px 12px -5px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 8px 13px -5px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 8px 13px -5px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 8px 14px -5px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 9px 14px -6px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 9px 15px -6px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],
})

export type MUIColors =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning'

type CreditJourneyPalette = {
  switchTrack: string
  switchBorder: string
  switchOff: string
  switchOffText: string
  ribbon: string
  ribbonText: string
  benefitText: string
  gain: string
  loss: string
}

declare module '@mui/material/styles' {
  interface Palette {
    productSwitcher: {
      front: string
      back: string
      dashed: string
    }
    creditJourney: CreditJourneyPalette
  }
  interface PaletteOptions {
    productSwitcher?: {
      front: string
      back: string
      dashed: string
    }
    creditJourney?: CreditJourneyPalette
  }
  interface Components {
    MuiLoadingButton?: {
      defaultProps?: ComponentsProps['MuiButton']
      styleOverrides?: ComponentsOverrides['MuiButton']
      variants?: ComponentsVariants['MuiButton']
    }
  }
}
